import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaExclamationTriangle, FaTimes } from 'react-icons/fa';

/**
 * Themed error modal — replaces native `alert()` calls with a centered,
 * theme-consistent dialog so users see the actual server message instead
 * of the generic "Failed to place order" toast.
 *
 * Usage:
 *   const { showError } = useErrorModal();
 *   showError('Specific message', { title: 'Optional title' });
 *
 * Wrap your app in <ErrorModalProvider> once.
 */

const ErrorModalContext = createContext({
  showError: () => {},
  hideError: () => {},
});

export const useErrorModal = () => useContext(ErrorModalContext);

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100000;
  padding: 1rem;
`;

const Card = styled(motion.div)`
  background: linear-gradient(135deg, rgba(20, 20, 20, 0.99) 0%, rgba(28, 28, 28, 0.99) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 1.75rem 1.75rem 1.5rem;
  max-width: 460px;
  width: 100%;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.7);
  position: relative;
`;

const CloseX = styled.button`
  position: absolute;
  top: 0.9rem;
  right: 0.9rem;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.55);
  cursor: pointer;
  font-size: 0.85rem;
  padding: 0.35rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
  }
`;

const IconWrap = styled.div`
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  background: ${p => p.theme?.colors?.dangerGradient || 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'};
  color: #fff;
  font-size: 1.25rem;
  box-shadow: 0 0 0 6px ${p => p.theme?.colors?.dangerGlow || 'rgba(239, 68, 68, 0.22)'};
`;

const Title = styled.h2`
  font-size: 1.15rem;
  font-weight: 700;
  color: ${p => p.theme?.colors?.text?.primary || '#fff'};
  margin: 0 0 0.6rem;
  text-align: center;
`;

const Message = styled.p`
  font-size: 0.92rem;
  color: ${p => p.theme?.colors?.text?.secondary || 'rgba(255,255,255,0.72)'};
  line-height: 1.6;
  text-align: center;
  margin: 0 0 1.25rem;
  white-space: pre-wrap;
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.6rem;
`;

const PrimaryButton = styled.button`
  padding: 0.6rem 1.4rem;
  border-radius: 10px;
  border: none;
  background: ${p => p.theme?.colors?.primaryGradient || 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)'};
  color: #fff;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 6px 16px ${p => p.theme?.colors?.primaryGlow || 'rgba(20, 184, 166, 0.35)'};
  transition: transform 0.12s ease, filter 0.12s ease;

  &:hover { filter: brightness(1.08); transform: translateY(-1px); }
  &:active { transform: translateY(0); }
`;

const ModalView = ({ open, title, message, onClose }) => (
  <AnimatePresence>
    {open && (
      <Overlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <Card
          initial={{ opacity: 0, y: 16, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.97 }}
          transition={{ duration: 0.18 }}
          onClick={(e) => e.stopPropagation()}
        >
          <CloseX type="button" onClick={onClose} aria-label="Close">
            <FaTimes />
          </CloseX>
          <IconWrap>
            <FaExclamationTriangle />
          </IconWrap>
          <Title>{title}</Title>
          <Message>{message}</Message>
          <Actions>
            <PrimaryButton type="button" onClick={onClose}>OK</PrimaryButton>
          </Actions>
        </Card>
      </Overlay>
    )}
  </AnimatePresence>
);

export const ErrorModalProvider = ({ children }) => {
  const [state, setState] = useState({ open: false, title: 'Something went wrong', message: '' });

  const showError = useCallback((messageOrObj, opts) => {
    let title = 'Something went wrong';
    let message = '';
    if (typeof messageOrObj === 'string') {
      message = messageOrObj;
    } else if (messageOrObj && typeof messageOrObj === 'object') {
      message = messageOrObj.message || '';
      if (messageOrObj.title) title = messageOrObj.title;
    }
    if (opts?.title) title = opts.title;
    if (!message) message = 'An unexpected error occurred. Please try again.';
    setState({ open: true, title, message });
  }, []);

  const hideError = useCallback(() => setState((s) => ({ ...s, open: false })), []);

  // Expose the showError function to non-React callers (thunks, services).
  React.useEffect(() => {
    setGlobalErrorHandler(showError);
    return () => setGlobalErrorHandler(null);
  }, [showError]);

  const value = useMemo(() => ({ showError, hideError }), [showError, hideError]);

  return (
    <ErrorModalContext.Provider value={value}>
      {children}
      <ModalView open={state.open} title={state.title} message={state.message} onClose={hideError} />
    </ErrorModalContext.Provider>
  );
};

/**
 * Bridge so non-React code (thunks, services) can also surface themed errors.
 * Set once by the provider; thunks call setGlobalErrorHandler in a useEffect.
 */
let globalErrorHandler = null;
export const setGlobalErrorHandler = (fn) => { globalErrorHandler = fn; };
export const reportError = (messageOrObj, opts) => {
  if (typeof globalErrorHandler === 'function') {
    globalErrorHandler(messageOrObj, opts);
  } else if (typeof window !== 'undefined') {
    // Last-resort fallback so messages aren't lost before provider mounts.
    // eslint-disable-next-line no-alert
    window.alert(typeof messageOrObj === 'string' ? messageOrObj : messageOrObj?.message || 'Error');
  }
};

export default ErrorModalProvider;
